import { useMemo, useId } from 'react';

import { useDropzone } from 'react-dropzone';

import { FileError, Label, HelperText } from '../common';

import { FileInputProps } from './FileInput.intefaces';
import { ALLOWED_TYPES } from './FileInput.constants';
import { FileInputField } from './FileInputField';
import { FileInputPreview } from './FileInputPreview';
import { useFileInput } from './hooks';

export type {
  FileInputProps,
  ValidateType,
  AspectRatioOption,
} from './FileInput.intefaces';

/**
 * Компонент FileInput представляет собой пользовательский интерфейс для загрузки файлов.
 * Он поддерживает различные варианты отображения (input и dropzone) и предоставляет функциональность
 * для предварительного просмотра, удаления и поворота изображений.
 *
 * @public
 * @param id - Атрибут ID для поля ввода.
 * @param value - Текущее значение компонента.
 * @param onChange - Функция, вызываемая при изменении значения.
 * @param onBlur - Функция, вызываемая при потере фокуса.
 * @param label - Текст метки для компонента.
 * @param labelIcon - Иконка для метки.
 * @param tooltip - Текст тултипа для метки.
 * @param variant - Вариант отображения компонента (input или dropzone).
 * @param placeholder - Текст плейсхолдера.
 * @param required - Флаг, указывающий, является ли поле обязательным.
 * @param disabled - Флаг, указывающий на отключенное состояние компонента.
 * @param loading - Флаг, указывающий на состояние загрузки.
 * @param loadingText - Текст загрузки.
 * @param cache - Функция для кэширования файла.
 * @param fullWidth - Флаг, указывающий на использование всей доступной ширины.
 * @param error - Флаг, указывающий на наличие ошибки.
 * @param errorText - Текст ошибки.
 * @param helperText - Дополнительный текст подсказки.
 * @param onCacheSuccess - Функция, вызываемая при успешном кэшировании.
 * @param onCacheError - Функция, вызываемая при ошибке кэширования.
 * @param bannerText - Текст баннера.
 * @param autorecognitionQr - Флаг автораспознавания QR.
 * @param testId - Тестовый идентификатор для компонента.
 * @param withAuth - Флаг, указывающий необходимо ли передавать токен авторизации.
 * @param withZoom - Флаг для масштабирования документа.
 * @param validators - Валидаторы.
 * @param allowedTypes - Допустимые типы файлов.
 * @returns  Компонент FileInput.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link FileInputProps}
 */
export const FileInput = ({
  id: htmlId,
  value,
  onChange,
  onBlur = () => {},
  label,
  labelIcon,
  tooltip,
  variant = 'input',
  placeholder = 'Перенесите файл, что бы прикрепить документ',
  required = false,
  disabled = false,
  loading = false,
  loadingText = undefined,
  cache,
  fullWidth,
  error = false,
  errorText = '',
  helperText = '',
  onCacheSuccess = () => {},
  onCacheError = () => {},
  bannerText,
  autorecognitionQr = false,
  testId = 'test-id-file-input',
  withAuth,
  withZoom = false,
  validators,
  allowedTypes = ALLOWED_TYPES,
  ...rest
}: FileInputProps) => {
  const reactId = useId();
  const inputId = useMemo(() => {
    return htmlId ?? reactId;
  }, [htmlId, reactId]);
  const {
    isLoading,
    isDisabled,
    validationErrorMessage,
    invalidFileName,
    load,
    blobDocument,
    cacheFileName,
    handleChange,
    onDrop,
    rotateDocument,
  } = useFileInput({
    value,
    onChange,
    onBlur,
    loading,
    disabled,
    cache,
    onCacheSuccess,
    onCacheError,
    withAuth,
    validators,
  });
  const finalError = error || validationErrorMessage;

  const { open, getRootProps, getInputProps } = useDropzone({
    disabled: isDisabled,
    onDrop,
  });

  /**
   * Функция removeDocument удаляет загруженный документ.
   * Вызывает handleChange с undefined для очистки значения.
   */
  const removeDocument = () => {
    handleChange(undefined);
  };

  const errorDisplay = useMemo(() => {
    if (validationErrorMessage) {
      return <FileError text={validationErrorMessage} />;
    }
    if (error) {
      return <FileError text={errorText} />;
    }
    if (helperText && !value) {
      return <HelperText text={helperText} />;
    }
    return null;
  }, [error, errorText, validationErrorMessage, helperText, value]);

  const inputLabel = label ? (
    <Label
      label={label}
      required={required}
      tooltip={tooltip}
      icon={variant === 'input' ? labelIcon : undefined}
      inputId={inputId}
    />
  ) : null;

  const viewProps = {
    value,
    disabled,
    fullWidth,
    inputId,
    testId,
    inputLabel,
    errorDisplay,
    finalError,
    isLoading,
    isDisabled,
    inputProps: rest,
    getRootProps,
    getInputProps,
    onLoad: load,
    onRemove: removeDocument,
  };

  return variant === 'input' ? (
    <FileInputField
      {...viewProps}
      withZoom={withZoom}
      cacheFileName={cacheFileName}
      label={label}
      placeholder={placeholder}
      invalidFileName={invalidFileName}
      hasValidationError={!!validationErrorMessage}
      onOpen={open}
    />
  ) : (
    <FileInputPreview
      {...viewProps}
      error={error}
      bannerText={bannerText}
      loadingText={loadingText}
      placeholder={placeholder}
      autorecognitionQr={autorecognitionQr}
      onRotate={rotateDocument}
      blobDocument={blobDocument}
      allowedTypes={allowedTypes}
    />
  );
};
